import _ from 'lodash';
import {
    PayerAdjustmentGroupType,
    ProviderInstitutionalClaimTemplate,
    ProviderProfessionalClaimTemplate,
    SupportInstitutionalClaimTemplate,
    SupportProfessionalClaimTemplate,
} from '../../../api';
import { AdjustmentCodes, formatCodeChip, isPatientResponsible, parseDenialCode, RemarkCodes } from '../../../services';
import { PaymentItemKeyInput } from '../matching-keys';

type ClaimTemplate =
    | ProviderProfessionalClaimTemplate
    | ProviderInstitutionalClaimTemplate
    | SupportProfessionalClaimTemplate
    | SupportInstitutionalClaimTemplate;

export interface IMismatchDescription {
    full: string;
    short: string;
    isNoLikelyMatch: boolean;
}

export interface IMismatchGroupMeta {
    contextLabel?: string;
    receivedTotal: number;
}

export interface IMismatchGroupPayment {
    id?: string;
    receivedDate?: string;
    paymentDate?: string;
}

export interface IMismatchGroupRecord {
    paid?: number;
    items?: Array<{ paymentId?: string }>;
}

const normalizeProcedureCode = (value?: string): string | undefined => {
    const trimmed = value ? _.trim(value) : undefined;

    return trimmed ? _.toUpper(trimmed) : undefined;
};

const normalizeDos = (value?: string): string | undefined => {
    const trimmed = value ? _.trim(value) : undefined;

    return trimmed || undefined;
};

export const findLikelyMatchLineNumber = (
    template: ClaimTemplate | undefined,
    item: PaymentItemKeyInput,
): number | undefined => {
    if (!template) {
        return undefined;
    }

    const itemProc = normalizeProcedureCode(item.procedureCode);
    const itemDos = normalizeDos(item.fromDos);

    if (!itemProc) {
        return undefined;
    }

    if ('f24ServiceLine' in template) {
        const lines = template.f24ServiceLine || [];
        const matchIndex = lines.findIndex(line => {
            const lineProc = normalizeProcedureCode(line.f24dProcedureCode);
            const lineDos = normalizeDos(line.f24aFromDateOfService);

            if (lineProc !== itemProc) {
                return false;
            }

            return !itemDos || !lineDos || lineDos === itemDos;
        });

        return matchIndex >= 0 ? matchIndex + 1 : undefined;
    }

    if ('f42ClaimLines' in template) {
        const lines = template.f42ClaimLines || [];
        const matchIndex = lines.findIndex(line => {
            const lineProc = normalizeProcedureCode(line.f44ProcedureCode);
            const lineDos = normalizeDos(line.f45FromServiceDate);

            if (lineProc !== itemProc) {
                return false;
            }

            return !itemDos || !lineDos || lineDos === itemDos;
        });

        return matchIndex >= 0 ? matchIndex + 1 : undefined;
    }

    return undefined;
};

export const buildMismatchDescription = (
    item: PaymentItemKeyInput & { controlNumber?: string; x12ControlNumber?: string; throughDos?: string },
    lineNumber: number | undefined,
    formatDos: (from?: string, through?: string) => string | undefined,
): IMismatchDescription => {
    const detailParts: string[] = [];

    if (item.procedureCode) {
        detailParts.push(`CPT ${item.procedureCode}`);
    }

    const dos = formatDos(item.fromDos, item.throughDos);
    if (dos) {
        detailParts.push(`DOS ${dos}`);
    }

    const controlNumber = item.x12ControlNumber || item.controlNumber;
    if (controlNumber) {
        detailParts.push(`control #${controlNumber}`);
    }

    if (!lineNumber) {
        return {
            full: 'No likely match',
            short: 'No likely match',
            isNoLikelyMatch: true,
        };
    }

    const detail = detailParts.join(', ');
    const full = detail ? `Line ${lineNumber} · ${detail} differs.` : `Line ${lineNumber} differs.`;
    const short = item.procedureCode ? `Line ${lineNumber} · CPT ${item.procedureCode}` : `Line ${lineNumber}`;

    return {
        full,
        short: full.length > short.length ? short : full,
        isNoLikelyMatch: false,
    };
};

/** A CARC/RARC chip rendered on a service line: the code shown, its description for the hover, and whether it moves the balance to the patient. */
export interface IReasonCodeChip {
    code: string;
    description: string;
    isPatientResponsibility: boolean;
}

interface IReasonCodeSourceItem {
    adjustments?: Array<{ group?: PayerAdjustmentGroupType; reasonCode?: string }>;
    remarkCodes?: string[];
}

/**
 * The CARC/RARC chips for a line's matched payment items, deduplicated across items.
 *
 * Reuses `parseDenialCode`/`formatCodeChip`/`isPatientResponsible` from `services/denials`
 * rather than re-deriving the group prefix here — the same rule the denials dashboard and
 * worker triggers use (GP-16). An adjustment whose group/reasonCode does not parse as a
 * denial (malformed data) is dropped rather than shown as a broken chip.
 */
export const getLineReasonCodes = (items: IReasonCodeSourceItem[] = []): IReasonCodeChip[] => {
    const chips = new Map<string, IReasonCodeChip>();

    items.forEach(item => {
        (item.adjustments || []).forEach(adjustment => {
            const denial = parseDenialCode(adjustment.group, adjustment.reasonCode);
            if (!denial) {
                return;
            }
            const code = formatCodeChip(denial);
            if (chips.has(code)) {
                return;
            }
            chips.set(code, {
                code,
                description: AdjustmentCodes[denial.reasonCode as keyof typeof AdjustmentCodes] || code,
                isPatientResponsibility: isPatientResponsible(denial),
            });
        });

        (item.remarkCodes || []).forEach(rawCode => {
            const code = (rawCode || '').trim().toUpperCase();
            if (!code || chips.has(code)) {
                return;
            }
            chips.set(code, {
                code,
                description: RemarkCodes[code as keyof typeof RemarkCodes] || code,
                isPatientResponsibility: false,
            });
        });
    });

    return [...chips.values()];
};

/**
 * Adjustments = Charges − Allowed: the contractual write-off the provider absorbs.
 *
 * This is not a re-sum of CO/OA/PI/CR CAS amounts — it is derived from the same `allowed`
 * figure the rest of the row already shows, so it can never disagree with it. `undefined`
 * (not 0) when the line has not been adjudicated yet: a line with no matched payment has an
 * unknown adjustment, not a zero one (NS-3 — unknown is not none).
 */
export const getLineAdjustmentAmount = (
    hasPayment: boolean,
    totalCharges: number,
    allowed: number,
): number | undefined => (hasPayment ? totalCharges - allowed : undefined);

export type NetVsContractState = 'at' | 'under' | 'over';

export interface INetVsContract {
    /** Allowed − contracted (accepted) rate. Negative means the payer paid under contract. */
    net: number;
    /** Allowed as a percentage of the contracted rate. 100 = paid exactly at contract. */
    pct: number;
    state: NetVsContractState;
    /** The operands `net` and `pct` were computed from, so a label can never mix in other totals. */
    allowed: number;
    accepted: number;
}

/** Below this, a net difference is rounding noise, not a real under/overpayment. */
const NET_AT_CONTRACT_TOLERANCE = 0.005;

/**
 * How the payer's allowed amount compares to the chargemaster's contracted rate for this line.
 *
 * `undefined` — not a 0/'at contract' result — when there is nothing to compare: no payment
 * has matched the line yet, or the chargemaster has no contracted rate on file for it. Showing
 * "at contract" in either case would be inventing a comparison that was never made (NS-3, NS-4).
 */
export const getNetVsContract = (
    hasPayment: boolean,
    allowed: number,
    accepted: number,
): INetVsContract | undefined => {
    if (!hasPayment || !accepted) {
        return undefined;
    }

    const net = allowed - accepted;
    const pct = (allowed / accepted) * 100;
    const state: NetVsContractState =
        Math.abs(net) < NET_AT_CONTRACT_TOLERANCE ? 'at' : net < 0 ? 'under' : 'over';

    return { net, pct, state, allowed, accepted };
};

export const buildMismatchGroupMeta = (
    mismatchedRecords: IMismatchGroupRecord[],
    payments: IMismatchGroupPayment[],
    payerTitle?: string,
    formatReceivedDate?: (date: string) => string,
): IMismatchGroupMeta => {
    const paymentIds = new Set(
        _.compact(_.flatMap(mismatchedRecords, record => (record.items || []).map(item => item.paymentId))),
    );

    const relatedPayments = payments.filter(payment => payment.id && paymentIds.has(payment.id));
    const receivedDatePayment = _.maxBy(
        relatedPayments,
        payment => payment.receivedDate || payment.paymentDate || '',
    );
    const receivedDate = receivedDatePayment?.receivedDate || receivedDatePayment?.paymentDate;

    const contextParts = _.compact([
        payerTitle,
        receivedDate ? `ERA received ${formatReceivedDate ? formatReceivedDate(receivedDate) : receivedDate}` : undefined,
    ]);

    const receivedTotal = mismatchedRecords.reduce((total, record) => total + (record.paid || 0), 0);

    return {
        contextLabel: contextParts.length ? contextParts.join(' · ') : undefined,
        receivedTotal,
    };
};
